import React, { useEffect, useState } from 'react'
import { Button, Icon } from 'semantic-ui-react'

import { useAppSelector } from 'redux/hooks'

import DeckService from 'services/deck.service'

import { CardType } from 'types/deck'
import LineLoader from 'components/elements/LineLoader'
import Select from 'components/formElements/SemanticSelect'
import AddRemoveCardRow from './addRemoveCardRow'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'

import { sortAddRemove, Column, Direction, generateFilterOptions, filterAddRemoveCards } from './addRemoveCard.utils'

import styles from './addRemoveCards.module.scss'

const filterByOptions: Array<{ text: string; value: Column }> = [
  { text: 'Card name', value: 'name' },
  { text: 'Action', value: 'action' },
  { text: 'Categories', value: 'categories' },
]

const AddRemoveCards = () => {
  const deckId = useAppSelector(state => state.deck.id)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cards, setCards] = useState(new Array<CardType>())
  const [orderBy, setOrderBy] = useState<Column>('timestamp')
  const [direction, setDirection] = useState<Direction>('desc')
  const [filterBy, setFilterBy] = useState<Column>('action')
  const [filter, setFilter] = useState(new Array<string>())

  const fetchCards = (skipLoading?: boolean) => {
    if (!skipLoading) setLoading(true)

    DeckService.cardsOnly(deckId)
      .then(setCards)
      .catch(err => {
        console.error(err)

        setError(err)
      })
      .finally(() => setLoading(false))
  }

  useEffect(fetchCards, [])
  useEffect(() => setFilter([]), [filterBy])

  const handleSort = (column: Column) => {
    if (column === orderBy && direction === 'asc') return setDirection('desc')
    if (column === orderBy && direction === 'desc') return setDirection('asc')

    setOrderBy(column)
    setDirection('asc')
  }

  if (loading) return <LineLoader />
  if (error) return <div>Some error occured when fetching your decks added and removed cards</div>

  const data = sortAddRemove(cards, direction, orderBy)
  const filterOptions = generateFilterOptions(data, filterBy).map(option => ({ text: option, value: option }))
  const filteredData = filter.length ? filterAddRemoveCards(data, filterBy, filter) : data

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.row}>
          <Select
            label="Filter By:"
            value={filterBy}
            containerClassName={styles.filters}
            onChange={(value: Column) => setFilterBy(value)}
            options={filterByOptions}
          />

          <Select
            search
            multiple
            containerClassName={styles.filters}
            placeholder="Added, Removed..."
            value={filter}
            label="Filter:"
            onChange={(value: string[]) => setFilter(value)}
            options={filterOptions}
          />
        </div>

        <ArchidektDropdown
          menuClassName={styles.keyDropdown}
          options={[
            { type: 'header', label: <GreenKey /> },
            { type: 'header', label: <RedKey /> },
            { type: 'header', label: <OrangeKey /> },
            { type: 'header', label: <BlueKey /> },
          ]}>
          <Button fluid>Color Key</Button>
        </ArchidektDropdown>
      </div>

      <table className={styles.table}>
        <tbody>
          <tr>
            <th />
            <th>
              <HeaderButton direction={direction} onClick={() => handleSort('name')} selected={orderBy === 'name'}>
                Name
              </HeaderButton>
            </th>
            <th>
              <HeaderButton direction={direction} onClick={() => handleSort('action')} selected={orderBy === 'action'}>
                Action
              </HeaderButton>
            </th>
            <th className={styles.categories}>
              <HeaderButton
                direction={direction}
                onClick={() => handleSort('categories')}
                selected={orderBy === 'categories'}>
                Current Categories
              </HeaderButton>
            </th>
            <th>
              <HeaderButton
                direction={direction}
                onClick={() => handleSort('timestamp')}
                selected={orderBy === 'timestamp'}>
                When
              </HeaderButton>
            </th>
            <th />
          </tr>
          {filteredData.map((card, index) => (
            <AddRemoveCardRow card={card} key={`${card.id}-${index}`} onUpdateCardList={() => fetchCards(true)} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AddRemoveCards

type HeaderButtonProps = {
  children: React.ReactNode
  onClick: () => void
  selected: boolean
  direction: Direction
}

const HeaderButton = ({ children, onClick, selected, direction }: HeaderButtonProps) => (
  <button className={styles.columnControls} onClick={() => onClick()}>
    {children}
    {selected && (
      <Icon name="chevron up" className={`${styles.orderByChevron} ${direction === 'desc' && styles.flip}`} />
    )}
  </button>
)

const GreenKey = () => (
  <label className={styles.row}>
    <span className={styles.greenBox} />
    <span>In deck</span>
  </label>
)

const RedKey = () => (
  <label className={styles.row}>
    <span className={styles.redBox} />
    <span>Removed from all categories</span>
  </label>
)

const OrangeKey = () => (
  <label className={styles.row}>
    <span className={styles.orangeBox} />
    <span>In deck, in a "not included" category</span>
  </label>
)

const BlueKey = () => (
  <label className={styles.row}>
    <span className={styles.blueBox} />
    <span>Potentially outdated item</span>
  </label>
)
